
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useGlobalState } from '../../utils/GlobalState';
import { decodedToken } from '../../utils/auth';
import Swal from 'sweetalert2';

const DisagreeJudgement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state: locationState = {} } = location;
  const {
    suggestion: initialSuggestion = '',
    imageSrc = '',
    showAnalysis = false,
    analysisResult = null,
    predictions = [],
    rawImageFile = null,
    predictedImageUrl = '',
  } = locationState;
  const { state: globalState, updateState } = useGlobalState();
  const [suggestion, setSuggestion] = useState(initialSuggestion);
  const [error, setError] = useState(null);
  const decoded = decodedToken();
  const userEmail = decoded?.email || '';
  const companyInfo = decoded?.companyInfo || '';
  const stablePredictions = useMemo(() => predictions, [predictions]);

  useEffect(() => {
    const initialGlobalState = {
      predictions: globalState?.predictions || stablePredictions || [],
      rawImageFile: globalState?.rawImageFile || rawImageFile || null,
      imageSrc: globalState?.imageSrc || imageSrc || '',
      showAnalysis: globalState?.showAnalysis || showAnalysis || false,
      analysisResult: globalState?.analysisResult || analysisResult || null,
      email: globalState?.email || userEmail || '',
      companyInfo: globalState?.companyInfo || companyInfo || '',
      predictedImageUrl: globalState?.predictedImageUrl || predictedImageUrl || '',
    };
    if (
      initialGlobalState.predictions !== globalState?.predictions ||
      initialGlobalState.rawImageFile !== globalState?.rawImageFile ||
      initialGlobalState.imageSrc !== globalState?.imageSrc ||
      initialGlobalState.showAnalysis !== globalState?.showAnalysis ||
      initialGlobalState.analysisResult !== globalState?.analysisResult ||
      initialGlobalState.email !== globalState?.email ||
      initialGlobalState.companyInfo !== globalState?.companyInfo ||
      initialGlobalState.predictedImageUrl !== globalState?.predictedImageUrl
    ) {
      updateState(initialGlobalState);
    }
  }, [
    stablePredictions,
    rawImageFile,
    imageSrc,
    showAnalysis,
    analysisResult,
    userEmail,
    companyInfo,
    predictedImageUrl,
    updateState,
    globalState?.predictions,
    globalState?.rawImageFile,
    globalState?.imageSrc,
    globalState?.showAnalysis,
    globalState?.analysisResult,
    globalState?.email,
    globalState?.companyInfo,
    globalState?.predictedImageUrl,
  ]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      updateState({
        predictions: [],
        rawImageFile: null,
        imageSrc: '',
        showAnalysis: false,
        analysisResult: null,
        email: '',
        companyInfo: '',
        predictedImageUrl: '',
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [updateState]);

  const savedPredictions = Array.isArray(globalState?.predictions)
    ? globalState.predictions
    : stablePredictions;
  const savedRawImageFile = globalState?.rawImageFile || rawImageFile;
  const savedImageSrc = globalState?.imageSrc || imageSrc;
  const savedShowAnalysis = globalState?.showAnalysis || showAnalysis;
  const savedAnalysisResult = globalState?.analysisResult || analysisResult;
  const savedPredictedImageUrl = globalState?.predictedImageUrl || predictedImageUrl;

  const handleBack = () => {
    const backState = {
      uploadedImage: savedImageSrc,
      showAnalysis: savedShowAnalysis,
      analysisResult: savedAnalysisResult,
      predictions: savedPredictions,
      rawImageFile: savedRawImageFile,
      predictedImageUrl: savedPredictedImageUrl,
    };
    updateState(backState);
    navigate('/upload-image-detection', { state: backState });
  };

  const handleRelabeling = () => {
    if (!suggestion.trim()) {
      setError('Please provide a suggestion before proceeding to re-labeling.');
      Swal.fire({
        title: 'Error!',
        text: 'Please provide a suggestion before proceeding to re-labeling.',
        icon: 'error',
        confirmButtonText: 'OK',
        background: 'white',
        customClass: {
          confirmButton: 'bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700',
          title: 'text-white',
          content: 'text-gray-300',
        },
      });
      return;
    }

    const relabelState = {
      suggestion,
      predictions: savedPredictions,
      rawImageFile: savedRawImageFile,
      imageSrc: savedImageSrc,
      showAnalysis: savedShowAnalysis,
      analysisResult: savedAnalysisResult,
      email: userEmail,
      companyInfo,
      predictedImageUrl: savedPredictedImageUrl,
    };
    updateState(relabelState);
    navigate('/Re-labeling', { state: relabelState });
  };

  if (!globalState || globalState.predictions === undefined) {
    return (
      <div className="w-full min-h-screen bg-black text-white flex items-center justify-center p-2 sm:p-4">
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-black text-white flex items-center justify-center p-2 sm:p-4">
      <div className="w-full max-w-4xl bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center mb-6">
          <button
            onClick={handleBack}
            className="p-2 bg-gray-800 text-white rounded-full hover:bg-gray-700 transition"
            title="Back"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h2 className="text-xl font-bold ml-4">Comments For Incorrect Judgment</h2>
        </div>
        {error && (
          <div className="mb-4 p-2 bg-red-600 text-white rounded">{error}</div>
        )}
        <div className="mb-8">
          <label className="block mb-2 text-lg font-semibold text-gray-300">Comments</label>
          <textarea
            className="w-full p-2 bg-gray-700 rounded mb-4 text-gray-300"
            value={suggestion}
            onChange={(e) => setSuggestion(e.target.value)}
            placeholder="Add your comments"
          ></textarea>
          <p className="text-gray-300">
            Please input the most likely issue and root cause in re-labeling. TPV member may contact you for further details to help improve system performance.
          </p>
        </div>
        <div className="mt-6">
          <button
            onClick={handleRelabeling}
            className={`w-full px-4 py-2 rounded-lg text-sm transition ${
              suggestion.trim()
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-500 text-gray-300 cursor-not-allowed'
            }`}
          >
            Next To Conduct Re-labeling
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisagreeJudgement;

